/**
 * Local Database (IndexedDB via Dexie.js)
 *
 * Purpose: Replace localStorage with robust, async, typed local database
 * Use cases:
 *   - Inventory cache (for offline access)
 *   - Offline queue (for sync when back online)
 *   - Profiles cache (to reduce profiles table scans)
 *
 * Architecture Decision #2: IndexedDB vs localStorage
 * - localStorage: 5MB limit, synchronous (blocks main thread)
 * - IndexedDB: ~50MB+ limit, asynchronous, faster
 *
 * Reference: LeltárMester Egyesített Refaktorálási Terv (v2.0)
 */

import Dexie, { type EntityTable } from 'dexie';
import { logger } from '@/services/logger';
import { safeParseArray } from '../utils/safeParse';

// ============================================================================
// Types
// ============================================================================

/**
 * Cached inventory item (for offline access)
 */
export interface CachedInventoryItem {
  ean: string; // Primary key
  name: string;
  quantity: number;
  price: number;
  netPrice: number;
  sellingPrice: number;
  netSellingPrice: number;
  vatRate: number;
  unit: string;
  sku: string;
  isWeighable: boolean;
  unitWeight: number | null;
  minQuantity: number | null;
  onShoppingList: boolean;
  companyId: string;
  version: number; // For optimistic locking
  cachedAt: number; // Timestamp
}

/**
 * Offline queue operation (for sync when back online)
 */
export interface OfflineQueueItem {
  id?: number; // Auto-increment primary key
  timestamp: number;
  type: 'COUNT' | 'DELTA' | 'CHECKOUT'; // Strategy A (COUNT), B (DELTA), or POS CHECKOUT
  operation: string; // RPC function name
  payload: unknown; // JSON payload for RPC
  retries: number;
  lastError: string | null;
  status: 'pending' | 'processing' | 'failed' | 'success';
}

/**
 * Cached profile (to reduce profiles table scans)
 */
export interface CachedProfile {
  userId: string; // Primary key
  companyId: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  cachedAt: number; // Timestamp
}

// ============================================================================
// Database Schema
// ============================================================================

/**
 * InventoryDatabase schema
 *
 * Tables:
 *   - inventoryCache: Cached inventory items
 *   - offlineQueue: Offline operations queue
 *   - profiles: Cached user profiles
 */
class InventoryDatabase extends Dexie {
  // Declare tables with types
  inventoryCache!: EntityTable<CachedInventoryItem, 'ean'>;
  offlineQueue!: EntityTable<OfflineQueueItem, 'id'>;
  profiles!: EntityTable<CachedProfile, 'userId'>;

  constructor() {
    super('LeltarMesterDB');

    // Define schema version 1
    this.version(1).stores({
      // inventoryCache: indexed by ean, also index companyId for filtering
      inventoryCache: 'ean, companyId, cachedAt',

      // offlineQueue: auto-increment id, indexed by timestamp and status
      offlineQueue: '++id, timestamp, status, type',

      // profiles: indexed by userId, also index companyId
      profiles: 'userId, companyId, cachedAt',
    });
  }
}

// ============================================================================
// Database Instance (Singleton)
// ============================================================================

/**
 * Singleton instance of the database
 */
export const db = new InventoryDatabase();

// ============================================================================
// Database Helpers
// ============================================================================

/**
 * Clear all cached data (useful for logout or reset)
 */
export async function clearAllCache(): Promise<void> {
  try {
    await db.inventoryCache.clear();
    await db.profiles.clear();
    logger.info('All cache cleared', undefined, 'DB');
  } catch (error) {
    logger.error('Failed to clear cache', error, 'DB');
    throw error;
  }
}

/**
 * Clear expired cache entries (older than TTL)
 */
export async function clearExpiredCache(ttl: number = 24 * 60 * 60 * 1000): Promise<void> {
  const now = Date.now();
  const cutoff = now - ttl;

  try {
    // Clear expired inventory cache
    const expiredInventory = await db.inventoryCache.where('cachedAt').below(cutoff).delete();

    // Clear expired profiles cache
    const expiredProfiles = await db.profiles.where('cachedAt').below(cutoff).delete();

    logger.debug(
      `Cleared ${expiredInventory} expired inventory items and ${expiredProfiles} expired profiles`,
      undefined,
      'DB'
    );
  } catch (error) {
    logger.error('Failed to clear expired cache', error, 'DB');
    throw error;
  }
}

/**
 * Get database statistics
 */
export async function getDatabaseStats() {
  try {
    const inventoryCount = await db.inventoryCache.count();
    const queueCount = await db.offlineQueue.count();
    const profilesCount = await db.profiles.count();

    return {
      inventoryCache: inventoryCount,
      offlineQueue: queueCount,
      profiles: profilesCount,
      totalSize: await estimateDatabaseSize(),
    };
  } catch (error) {
    logger.error('Failed to get database stats', error, 'DB');
    return {
      inventoryCache: 0,
      offlineQueue: 0,
      profiles: 0,
      totalSize: 0,
    };
  }
}

/**
 * Estimate database size (approximate)
 */
async function estimateDatabaseSize(): Promise<number> {
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    const estimate = await navigator.storage.estimate();
    return estimate.usage || 0;
  }
  return 0;
}

/**
 * Delete database (for debugging or reset)
 */
export async function deleteDatabaseCompletely(): Promise<void> {
  try {
    await db.delete();
    logger.warn('Database deleted completely', undefined, 'DB');
  } catch (error) {
    logger.error('Failed to delete database', error, 'DB');
    throw error;
  }
}

// ============================================================================
// Migration Helpers (for future schema changes)
// ============================================================================

/**
 * Migrate from localStorage to IndexedDB
 *
 * This is a one-time migration for users upgrading from the old localStorage system.
 * Can be removed after all users have migrated.
 */
export async function migrateFromLocalStorage(): Promise<void> {
  try {
    // Check if migration already done
    const migrationFlag = localStorage.getItem('indexeddb_migration_done');
    if (migrationFlag === 'true') {
      logger.debug('IndexedDB migration already done', undefined, 'DB');
      return;
    }

    // Migrate offline queue (if exists)
    const oldQueue = localStorage.getItem('offline_queue');
    if (oldQueue) {
      try {
        const parsedQueue = safeParseArray(oldQueue, []);
        if (Array.isArray(parsedQueue) && parsedQueue.length > 0) {
          // Convert old queue format to new format
          const newQueue: OfflineQueueItem[] = parsedQueue.map((item, index) => ({
            timestamp: item.timestamp || Date.now() - index * 1000,
            type: item.type || 'DELTA',
            operation: item.operation || 'adjust_inventory_delta',
            payload: item.payload || item,
            retries: item.retries || 0,
            lastError: null,
            status: 'pending' as const,
          }));

          await db.offlineQueue.bulkAdd(newQueue);
          logger.info(`Migrated ${newQueue.length} offline queue items`, undefined, 'DB');
        }
      } catch (parseError) {
        logger.warn('Failed to parse old offline queue', parseError, 'DB');
      }
    }

    // Mark migration as done
    localStorage.setItem('indexeddb_migration_done', 'true');
    logger.info('IndexedDB migration complete', undefined, 'DB');
  } catch (error) {
    logger.error('IndexedDB migration failed', error, 'DB');
    // Don't throw - allow app to continue even if migration fails
  }
}

// ============================================================================
// Initialization
// ============================================================================

/**
 * Initialize database
 * - Run migrations
 * - Clear expired cache
 * - Log statistics
 */
export async function initializeDatabase(): Promise<void> {
  try {
    // Run localStorage migration (one-time)
    await migrateFromLocalStorage();

    // Clear expired cache (24 hours TTL)
    await clearExpiredCache(24 * 60 * 60 * 1000);

    // Log statistics
    const stats = await getDatabaseStats();
    logger.info('Database initialized', stats, 'DB');
  } catch (error) {
    logger.error('Database initialization failed', error, 'DB');
    // Don't throw - allow app to continue even if initialization fails
  }
}

// Auto-initialize on import (optional)
// Uncomment if you want automatic initialization:
// initializeDatabase().catch(console.error);
